import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Loader2, FileText, RotateCcw, Truck, StickyNote, Download, RefreshCw, Activity } from 'lucide-react';
import { format } from 'date-fns';

const ACTION_CONFIG = {
  status_change:   { icon: Activity,    color: 'text-blue-400' },
  tracking_update: { icon: Truck,       color: 'text-purple-400' },
  internal_note:   { icon: StickyNote,  color: 'text-yellow-400' },
  refund:          { icon: RotateCcw,   color: 'text-orange-400' },
  shipment_added:  { icon: Truck,       color: 'text-green-400' },
  download_added:  { icon: Download,    color: 'text-primary' },
  reconciled:      { icon: RefreshCw,   color: 'text-white/40' },
  royal_mail_dispatch: { icon: Truck,   color: 'text-primary' },
  royal_mail_reissue:  { icon: RefreshCw, color: 'text-amber-400' },
};

export default function OrderAuditLogPanel({ orderId }) {
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ['order-audit', orderId],
    queryFn: () => base44.entities.OrderAuditLog.filter({ order_id: orderId }, '-created_date'),
  });

  if (isLoading) return <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>;
  if (logs.length === 0) return <p className="text-white/20 text-xs text-center py-4">No audit events yet</p>;

  return (
    <div className="space-y-1.5 max-h-64 overflow-y-auto">
      {logs.map(log => {
        const cfg = ACTION_CONFIG[log.action] || { icon: FileText, color: 'text-white/40' };
        const Icon = cfg.icon;
        return (
          <div key={log.id} className="flex items-start gap-2.5 py-2 border-b border-white/5 last:border-0">
            <div className={`w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 ${cfg.color}`}>
              <Icon className="w-3 h-3" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white/60 text-xs leading-snug">{log.description}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-white/25 text-[10px]">{log.admin_name || log.admin_email}</span>
                <span className="text-white/15 text-[10px]">·</span>
                <span className="text-white/25 text-[10px]">
                  {log.created_date ? format(new Date(log.created_date), 'dd MMM yyyy HH:mm') : '—'}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}